import { useCallback, useMemo } from "react";
import { usePostHog as usePostHogContext } from "../contexts/PostHogContext";

/**
 * Hook to use PostHog analytics
 * Provides easy-to-use methods for tracking events
 *
 * @returns {Object} PostHog methods and utilities
 */
export const usePostHog = () => {
  const { track, isInitialized, posthog } = usePostHogContext();

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {Object} properties - Additional properties to send with the event
   */
  const trackEvent = useCallback(
    (eventName, properties = {}) => {
      if (!isInitialized) return;

      track(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
    },
    [track, isInitialized],
  );

  /**
   * Track page view
   * @param {string} pageName - Name of the page
   * @param {Object} properties - Additional properties
   */
  const trackPageView = useCallback(
    (pageName, properties = {}) => {
      if (!isInitialized) return;

      track("$pageview", {
        page_name: pageName,
        ...properties,
      });
    },
    [track, isInitialized],
  );
  /**
   * Check if a feature flag is enabled.
   *
   * @param {string} flagKey - Feature flag key
   * @param {boolean} defaultValue - Value to return when not ready / missing
   */
  const isFeatureEnabled = useCallback(
    (flagKey, defaultValue = false) => {
      if (!isInitialized) return defaultValue;
      if (!flagKey) return defaultValue;

      try {
        const isEnabled = posthog?.isFeatureEnabled?.(flagKey);
        if (typeof isEnabled !== "boolean") return defaultValue;
        return isEnabled;
      } catch (error) {
        console.warn("PostHog isFeatureEnabled error:", error);
        return defaultValue;
      }
    },
    [isInitialized, posthog],
  );

  /**
   * Track activation events (onboarding)
   */
  const trackActivation = useMemo(
    () => ({
      whatsappConnected: (properties = {}) => {
        trackEvent("whatsapp_connected", properties);
      },
      menuUploaded: (properties = {}) => {
        trackEvent("menu_uploaded", properties);
      },
      paymentMethodAdded: (properties = {}) => {
        trackEvent("payment_method_added", properties);
      },
      firstBotOrder: (properties = {}) => {
        trackEvent("first_bot_order", {
          ...properties,
          // Calculate time-to-value if signup date is available
          time_to_value: properties.signup_date
            ? Math.round(
                (Date.now() - new Date(properties.signup_date).getTime()) /
                  (1000 * 60 * 60),
              ) // hours
            : null,
        });
      },
    }),
    [trackEvent],
  );

  /**
   * Track engagement events
   */
  const trackEngagement = useMemo(
    () => ({
      menuItemUpdated: (properties = {}) => {
        trackEvent("menu_item_updated", properties);
      },
      priceChanged: (properties = {}) => {
        trackEvent("price_changed", properties);
      },
      login: (properties = {}) => {
        trackEvent("login", properties);
      },
    }),
    [trackEvent],
  );

  /**
   * Track feature adoption events
   */
  const trackFeatureAdoption = useMemo(
    () => ({
      analyticsPageViewed: (properties = {}) => {
        trackEvent("analytics_page_viewed", properties);
      },
      liveChatOpened: (properties = {}) => {
        trackEvent("live_chat_opened", properties);
      },
      dataExported: (properties = {}) => {
        trackEvent("data_exported", {
          ...properties,
          export_type: properties.export_type || "unknown",
        });
      },
    }),
    [trackEvent],
  );

  return {
    trackEvent,
    trackPageView,
    isFeatureEnabled,
    trackActivation,
    trackEngagement,
    trackFeatureAdoption,
    isInitialized,
  };
};

export default usePostHog;
